import { escapeAttr } from '../html.js';

// Variants are produced by scripts/optimize-screenshots.mjs from the originals
// in apps/landing/screenshots/; keep these in sync with its WIDTHS.
export const THUMB_WIDTHS = [480, 960] as const;
export const THUMB_FORMATS = ['avif', 'webp', 'jpg'] as const;
const THUMB_DIR = '/screenshots/';

// Cards sit two-up inside the 68.75rem page (~29rem each), one-up below the
// 45rem mobile breakpoint, where the tile's own padding eats ~5rem.
const THUMB_SIZES = '(max-width: 45rem) calc(100vw - 5rem), 29rem';

// Intrinsic-size hint matching the .game-thumb frame's 16:10 aspect ratio, so
// the box is reserved before the image arrives (object-fit: cover crops the
// ~16:9 screenshots into it).
const THUMB_ATTR_WIDTH = 960;
const THUMB_ATTR_HEIGHT = 600;

const MIME: Record<(typeof THUMB_FORMATS)[number], string> = {
  avif: 'image/avif',
  webp: 'image/webp',
  jpg: 'image/jpeg',
};

export function thumbUrl(name: string, width: number, format: string): string {
  return `${THUMB_DIR}${name}-${width}.${format}`;
}

function srcset(name: string, format: string): string {
  return THUMB_WIDTHS.map((w) => `${thumbUrl(name, w, format)} ${w}w`).join(', ');
}

export interface ThumbParams {
  /** Screenshot base name, e.g. `checkers` → `/screenshots/checkers-480.webp`. */
  readonly name: string;
  readonly alt: string;
  /**
   * Above-the-fold cards load eagerly — lazy-loading them left empty frames on
   * first paint. `high` also sets fetchpriority, for the first (LCP) card only.
   */
  readonly priority?: 'high' | 'eager';
}

export function renderThumb(p: ThumbParams): string {
  const sources = THUMB_FORMATS.filter((f) => f !== 'jpg')
    .map((f) => `<source type="${MIME[f]}" srcset="${srcset(p.name, f)}" sizes="${THUMB_SIZES}" />`)
    .join('');
  const loading = p.priority === undefined ? 'loading="lazy"' : 'loading="eager"';
  const fetchPriority = p.priority === 'high' ? ' fetchpriority="high"' : '';
  return `<picture>${sources}<img src="${thumbUrl(p.name, THUMB_WIDTHS[0], 'jpg')}" srcset="${srcset(p.name, 'jpg')}" sizes="${THUMB_SIZES}" width="${THUMB_ATTR_WIDTH}" height="${THUMB_ATTR_HEIGHT}" alt="${escapeAttr(p.alt)}" ${loading}${fetchPriority} decoding="async" /></picture>`;
}
